import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Json } from '@/integrations/supabase/types';
import {
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  reorderMenuItems,
  SupabaseServiceError,
  NewMenuItem,
  UpdatedMenuItem,
  SortOrderItem,
  fetchBranchMenuItems,
} from '@/services/menuService';
import { MenuItem } from '@/types/index';
import { useCachedData } from './useCache';
import { CacheKeys, UserContext } from '@/stores/cacheKeys';
import { TTL_CONFIG } from '@/stores/cacheConfig';
import { useCacheInvalidation } from './useCache';

export const useMenuItems = (categoryId?: string, branchId?: string, options?: { enabled?: boolean }) => {
  const { user, isStaff, staffSession } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateCache } = useCacheInvalidation();
  const enabled = options?.enabled ?? true;

  // Create user context for caching (memoized to prevent dependency issues)
  const userContext: UserContext | null = useMemo(() => {
    return user || staffSession ? {
      userId: user?.id || staffSession?.id || '',
      restaurantId: staffSession?.restaurant_id || '',
      isStaff: isStaff,
      allowedBranches: staffSession?.allowed_branches || []
    } : null;
  }, [user, isStaff, staffSession]);

  // Generate cache key - need both branchId and categoryId
  const cacheKey = userContext && categoryId && branchId ? CacheKeys.menuItems(userContext, branchId, categoryId) : '';

  // Define the fetcher function
  const fetchMenuItems = useCallback(async (): Promise<MenuItem[]> => {
    // Check if user is authenticated (either owner or staff)
    if (!categoryId || (!user && !isStaff) || !userContext) {
      return [];
    }

    // For staff users, we need to check if they have access to the branch
    // First, get the category to find the branch_id
    if (isStaff && staffSession) {
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('branch_id')
        .eq('id', categoryId)
        .single();

      if (categoryError) {
        throw new SupabaseServiceError('Failed to fetch category', categoryError);
      }

      // Check if staff has access to this branch
      if (staffSession.allowed_branches && staffSession.allowed_branches.length > 0) {
        if (!staffSession.allowed_branches.includes(categoryData.branch_id)) {
          throw new SupabaseServiceError('Access denied to this branch', new Error('Access denied'));
        }
      }
    }

    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .eq('category_id', categoryId)
      .order('sort_order', { ascending: true, nullsFirst: false });

    if (error) {
      throw new SupabaseServiceError('Failed to fetch menu items', error);
    }
    return (data as unknown as MenuItem[]) || [];
  }, [categoryId, user, isStaff, staffSession, userContext]);

  // Use both cached data and React Query for enhanced performance
  const {
    data: cachedMenuItems,
    loading: cacheLoading,
    error: cacheError,
    isFromCache,
    refetch: refetchCache,
    invalidate: invalidateCachedData
  } = useCachedData<MenuItem[]>(
    cacheKey,
    fetchMenuItems,
    {
      enabled: enabled && !!categoryId && !!branchId && !!userContext,
      ttl: TTL_CONFIG.menuItems,
      fallbackToCache: true,
      backgroundRefresh: true
    }
  );

  // Also use React Query for mutation support and additional caching
  const queryKey = ['menuItems', categoryId];
  const { data: queryMenuItems, isLoading: queryLoading, error: queryError, refetch: refetchQuery } = useQuery<MenuItem[], SupabaseServiceError>(
    {
      queryKey,
      queryFn: fetchMenuItems,
      enabled: enabled && !!categoryId && (!!user || !!isStaff) && !isFromCache && !cachedMenuItems, // Only fetch if no cache AND no cached data
      staleTime: TTL_CONFIG.menuItems,
    }
  );

  const createMenuItemMutation = useMutation<MenuItem, SupabaseServiceError, NewMenuItem>(
    {mutationFn: createMenuItem,
    onSuccess: async (newMenuItem) => {
      // Update React Query cache
      queryClient.setQueryData<MenuItem[]>(queryKey, (oldMenuItems) => {
        if (oldMenuItems) {
          return [...oldMenuItems, newMenuItem];
        }
        return [newMenuItem];
      });
      
      // Invalidate our custom cache
      await invalidateCache(['menuItems']);
    },
    onError: (err) => {
      console.error('Error creating menu item:', err);
      queryClient.invalidateQueries({ queryKey });
    },
  }
  );

  const updateMenuItemMutation = useMutation<MenuItem, SupabaseServiceError, { id: string; updates: UpdatedMenuItem }, { previousMenuItems: MenuItem[] | undefined }>(
    {mutationFn: ({ id, updates }) => updateMenuItem(id, updates),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousMenuItems = queryClient.getQueryData<MenuItem[]>(queryKey);
      queryClient.setQueryData<MenuItem[]>(queryKey, (oldMenuItems) => {
        return oldMenuItems ? oldMenuItems.map(item => item.id === id ? { ...item, ...updates } : item) : [];
      });
      return { previousMenuItems };
    },
    onError: (err, variables, context) => {
      console.error('Error updating menu item:', err);
      if (context?.previousMenuItems) {
        queryClient.setQueryData<MenuItem[]>(queryKey, context.previousMenuItems);
      }
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey });
      await invalidateCache(['menuItems']);
    },
  }
  );

  const deleteMenuItemMutation = useMutation<void, SupabaseServiceError, string, { previousMenuItems: MenuItem[] | undefined }>(
    {mutationFn: deleteMenuItem,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previousMenuItems = queryClient.getQueryData<MenuItem[]>(queryKey);
      queryClient.setQueryData<MenuItem[]>(queryKey, (oldMenuItems) => {
        return oldMenuItems ? oldMenuItems.filter(item => item.id !== id) : [];
      });
      return { previousMenuItems };
    },
    onError: (err, id, context) => {
      console.error('Error deleting menu item:', err);
      if (context?.previousMenuItems) {
        queryClient.setQueryData<MenuItem[]>(queryKey, context.previousMenuItems);
      }
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey });
      await invalidateCache(['menuItems']);
    },
  }
  );

  const reorderMenuItemsMutation = useMutation<void, SupabaseServiceError, SortOrderItem[], { previousMenuItems: MenuItem[] | undefined }>(
    {mutationFn: async (newOrder) => {
      const { error } = await supabase.rpc('update_sort_order', {
        table_name: 'menu_items',
        items: newOrder.map(item => ({
          id: item.id,
          sort_order: item.sort_order
        }))
      });
      if (error) throw new SupabaseServiceError('Failed to reorder menu items', error);
    },
    onMutate: async (newOrder) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousMenuItems = queryClient.getQueryData<MenuItem[]>(queryKey);

      // Optimistically update to the new value
      if (previousMenuItems) {
        const updatedMenuItems = [...previousMenuItems];
        // Sort the items based on the new order
        updatedMenuItems.sort((a, b) => {
          const aOrder = newOrder.find(item => item.id === a.id)?.sort_order ?? 0;
          const bOrder = newOrder.find(item => item.id === b.id)?.sort_order ?? 0;
          return aOrder - bOrder;
        });
        queryClient.setQueryData<MenuItem[]>(queryKey, updatedMenuItems);
      }

      return { previousMenuItems };
    },
    onError: (err, newOrder, context) => {
      console.error('Error reordering menu items:', err);
      // If there was an error, roll back to the previous value
      if (context?.previousMenuItems) {
        queryClient.setQueryData<MenuItem[]>(queryKey, context.previousMenuItems);
      }
    },
    onSettled: async () => {
      // Always refetch after error or success to ensure we're up to date
      queryClient.invalidateQueries({ queryKey });
      await invalidateCache(['menuItems']);
    },
  }
  );

  // Determine which data to use (prefer cache, fallback to query)
  const menuItems = cachedMenuItems || queryMenuItems || [];
  // Only show loading if we have no data AND something is loading
  const loading = (cacheLoading || queryLoading) && !menuItems.length;
  const error = cacheError || queryError?.message || null;

  // Combined refetch function
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchCache(),
      refetchQuery()
    ]);
  }, [refetchCache, refetchQuery]);

  return {
    menuItems,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate: invalidateCachedData,
    createMenuItem: createMenuItemMutation.mutate,
    updateMenuItem: updateMenuItemMutation.mutate,
    deleteMenuItem: deleteMenuItemMutation.mutate,
    reorderMenuItems: reorderMenuItemsMutation.mutate,
    isCreatingMenuItem: createMenuItemMutation.isPending,
    isUpdatingMenuItem: updateMenuItemMutation.isPending,
    isDeletingMenuItem: deleteMenuItemMutation.isPending,
    isReorderingMenuItems: reorderMenuItemsMutation.isPending,
  };
};

export const useBranchMenuItems = (branchId: string) => {
  const { user, isStaff } = useAuth();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branch-menu-items', branchId],
    queryFn: () => fetchBranchMenuItems(branchId),
    enabled: !!branchId && (!!user || !!isStaff),
  });

  return {
    menuItems: data || [],
    isLoading,
    error,
    refetch
  };
};

